import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '../store';
import { AuthProvider } from '../contexts/AuthContext';
import { CartProvider } from '../contexts/CartContext';
import { WishlistProvider } from '../contexts/WishlistContext';
import { PaymentProvider } from '../contexts/PaymentContext';
import { SearchProvider } from '../contexts/SearchContext';
import { OrderTrackingProvider } from '../contexts/OrderTrackingContext';
import { AISupportProvider } from '../contexts/AISupportContext';
import pusherService from '../services/pusher';
import Layout from './Layout/Layout';
import ProtectedRoute from './Pages/ProtectedRoute';
import DynamicSEO from './SEO/DynamicSEO';
import Home from './Pages/Home';
import MarketplaceHome from './Pages/Marketplace/Home';
import MenuListing from './Pages/Marketplace/MenuListing';
import VendorListing from './Pages/Marketplace/VendorListing';
import MarketplaceDemo from './Pages/Marketplace/Demo';
import About from './Pages/About';
import Services from './Pages/Services';
import Contact from './Pages/Contact';
import Login from './Pages/Auth/Login';
import Register from './Pages/Auth/Register';
import Dashboard from './Pages/Dashboard/Dashboard';
import VendorDashboard from './Pages/Vendor/Dashboard';
import VendorProfile from './Pages/Vendor/Profile';
import VendorMenus from './Pages/Vendor/Menus';
import VendorCategories from './Pages/Vendor/Categories';
import VendorLeads from './Pages/Vendor/Leads';
import VendorBookings from './Pages/Vendor/Bookings';
import CustomerChat from './Pages/Customer/Chat';
import ChatPage from './Pages/Chat/ChatPage';
import GetStarted from './Pages/GetStarted';
import Wishlist from './Pages/Wishlist';
import Search from './Pages/Search';
import OrderTracking from './Pages/OrderTracking';
import Notifications from './Pages/Notifications';
import NotificationsRedirect from './Pages/NotificationsRedirect';
import AdminDashboard from './Pages/Admin/Dashboard';
import ActivityPage from './Pages/Activity/ActivityPage';
import Settings from './Pages/Settings/Settings';

// Wrapper component to handle URL parameters for GetStarted
function GetStartedWrapper() {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const editDraftId = params.get('edit');
    return <GetStarted existingDraftId={editDraftId} />;
}
import AdminCities from './Pages/Admin/Cities';
import AdminCategories from './Pages/Admin/Categories';
import AdminPackages from './Pages/Admin/Packages';
import AdminMenus from './Pages/Admin/Menus';
import AdminUsers from './Pages/Admin/Users';
import AdminVendors from './Pages/Admin/Vendors';
import AdminBookings from './Pages/Admin/Bookings';
import AdminLeads from './Pages/Admin/Leads';
import AdminContent from './Pages/Admin/Content';
import HomepageContent from './Pages/Admin/HomepageContent';
import SimpleHeroBanners from './Pages/Admin/SimpleHeroBanners';
import PaymentScheduleDemo from './PaymentSchedule/PaymentScheduleDemo';
import AdminAnalytics from './Pages/Admin/Analytics';
import LogViewer from './Pages/Admin/LogViewer';
import AdminSettings from './Pages/Admin/Settings';
import EmailConfiguration from './Pages/Admin/EmailConfiguration';
import EmailTesting from './Pages/Admin/EmailTesting';
import AdminReports from './Pages/Admin/Reports';
import AdminLogs from './Pages/Admin/Logs';
import AdminBackup from './Pages/Admin/Backup';
import QuotesList from './Pages/Quotes/QuotesList';
import QuoteDetail from './Pages/Quotes/QuoteDetail';
import QuotesDebug from './Debug/QuotesDebug';
import AdminIntegrations from './Pages/Admin/Integrations';
import AdminNotifications from './Pages/Admin/Notifications';
import AdminSystemLogs from './Pages/Admin/SystemLogs';
import AdminBackups from './Pages/Admin/Backups';
import ContentDemo from './Pages/Admin/ContentDemo';
import EmailTemplates from './Pages/Admin/EmailTemplates';
import Testimonials from './Pages/Admin/Testimonials';
import Features from './Pages/Admin/Features';
import Articles from './Pages/Admin/Articles';
import PaymentSettings from './Pages/Admin/PaymentSettings';
import AdminLayout from './Layout/AdminLayout';
import AdminSMTP from './Pages/Admin/SMTP';
import NotFound from './Pages/Errors/NotFound';
import Forbidden from './Pages/Errors/Forbidden';
import Menu from './Pages/Menu';
import MenuItemDetail from './Pages/MenuItemDetail';
import AITestPage from './Pages/AITestPage';
import Booking from './Pages/Booking';
import BookingShow from './Pages/Booking/Show';
import Checkout from './Pages/Checkout';
import BookingConfirmation from './Pages/BookingConfirmation';
import Profile from './Pages/Profile';
import ToastProvider from './UI/ToastProvider';
import FoodThemeProvider from './UI/FoodThemeProvider';
import ScrollAndFocusHelper from './UI/ScrollAndFocusHelper';
import ScrollRevealHelper from './UI/ScrollRevealHelper';
import ImagePickerProvider from './UI/ImagePickerProvider';
import MessageNotification from './UI/MessageNotification';
import LeadNotificationPopup from './UI/LeadNotificationPopup';
import VendorShow from './Pages/Vendor/Show';
import Vendors from './Pages/Vendors';
import HelpCenter from './Pages/HelpCenter';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import TermsOfService from './Pages/TermsOfService';
import FAQ from './Pages/FAQ';
import Quotes from './Pages/Quotes';

function App() {
    const location = useLocation();

    useEffect(() => {
        // Initialize Pusher Beams when app starts
        pusherService.initialize()
            .then(() => {
                console.log('Pusher Beams initialized successfully');
            })
            .catch((error) => {
                console.error('Failed to initialize Pusher Beams:', error);
            });
        try {
            // Ensure WebAudio can play after first interaction
            const mod = import('../services/soundNotification');
            // If dynamic import not desired, ensure it's loaded in bundle so side-effects are available
        } catch (_) {}
    }, []);

    return (
        <Provider store={store}>
            <AuthProvider>
                <CartProvider>
                    <WishlistProvider>
                        <PaymentProvider>
                            <SearchProvider>
                                <OrderTrackingProvider>
                                    <AISupportProvider>
                <ToastProvider>
                <FoodThemeProvider>
                <ImagePickerProvider>
                <DynamicSEO />
                <ScrollAndFocusHelper />
                <ScrollRevealHelper />
                <MessageNotification />
                <LeadNotificationPopup />
                <div key={location.pathname} className="page-fade-enter page-fade-enter-active">
                <Routes>
                    {/* Admin Routes - No Layout wrapper */}
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoute roles={["admin"]}>
                                <AdminLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="vendors" element={<AdminVendors />} />
                        <Route path="bookings" element={<AdminBookings />} />
                        <Route path="leads" element={<AdminLeads />} />
                        {/* Consolidated under Content */}
                        <Route path="cities" element={<Navigate to="/admin/content" replace />} />
                        <Route path="categories" element={<Navigate to="/admin/content" replace />} />
                        <Route path="packages" element={<Navigate to="/admin/content" replace />} />
                        <Route path="menus" element={<AdminMenus />} />
                        <Route path="analytics" element={<AdminAnalytics />} />
                        <Route path="content" element={<AdminContent />} />
                        <Route path="homepage" element={<HomepageContent />} />
                        <Route path="hero-banners" element={<SimpleHeroBanners />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="email-configuration" element={<EmailConfiguration />} />
                        <Route path="email-testing" element={<EmailTesting />} />
                        <Route path="email-templates" element={<EmailTemplates />} />
                        {/* SMTP Admin page */}
                        <Route path="smtp" element={<AdminSMTP />} />
                    {/* Managed under Content */}
                    <Route path="testimonials" element={<Navigate to="/admin/content" replace />} />
                    <Route path="features" element={<Navigate to="/admin/content" replace />} />
                    <Route path="articles" element={<Navigate to="/admin/content" replace />} />
                    <Route path="integrations" element={<AdminIntegrations />} />
                    <Route path="notifications" element={<AdminNotifications />} />
                    <Route path="payment-settings" element={<PaymentSettings />} />
                    <Route path="logs" element={<LogViewer />} />
                        <Route path="backups" element={<AdminBackups />} />
                        {/* Removed legacy/unimplemented routes: reports, backup, content-demo */}
                    </Route>

                    {/* All other routes with Layout wrapper */}
                    <Route path="/*" element={
                        <Layout>
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/" element={<Home />} />
                                <Route path="/marketplace" element={<MarketplaceHome />} />
                                <Route path="/menu-listing" element={<MenuListing />} />
                                <Route path="/vendor-listing" element={<VendorListing />} />
                                <Route path="/marketplace-demo" element={<MarketplaceDemo />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/services" element={<Services />} />
                                <Route path="/menu" element={<Menu />} />
                                <Route path="/menu/:id" element={<MenuItemDetail />} />
                                <Route path="/ai-test" element={<AITestPage />} />
                                <Route path="/vendors" element={<Vendors />} />
                                <Route path="/vendors/:id" element={<VendorShow />} />
                                <Route path="/wishlist" element={<Wishlist />} />
                                <Route path="/search" element={<Search />} />
                                <Route path="/notifications" element={<NotificationsRedirect />} />
                                <Route path="/user/notifications" element={<Notifications />} />
                                <Route path="/order-tracking" element={<OrderTracking />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/payment-schedule-demo" element={<PaymentScheduleDemo />} />
                                <Route path="/help-center" element={<HelpCenter />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/terms-of-service" element={<TermsOfService />} />
                                <Route path="/faq" element={<FAQ />} />
                                <Route path="/booking" element={<Navigate to="/get-started" replace />} />
                                <Route path="/quotes" element={<ProtectedRoute><QuotesList /></ProtectedRoute>} />
                                <Route path="/quotes/:id" element={<ProtectedRoute><QuoteDetail /></ProtectedRoute>} />
                                <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
                                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                                <Route path="/debug/quotes" element={<ProtectedRoute><QuotesDebug /></ProtectedRoute>} />
                                <Route path="/checkout" element={<Checkout />} />
                                <Route path="/booking-confirmation" element={<BookingConfirmation />} />
                                <Route
                                    path="/bookings/:id"
                                    element={
                                        <ProtectedRoute>
                                            <BookingShow />
                                        </ProtectedRoute>
                                    }
                                />
                                
                                {/* Auth Routes */}
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Register />} />
                                
                                {/* Protected Routes */}
                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute>
                                            <Dashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/activity"
                                    element={
                                        <ProtectedRoute>
                                            <ActivityPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <ProtectedRoute>
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/profile"
                                    element={
                                        <ProtectedRoute>
                                            <Profile />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/dashboard"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/menus"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorMenus />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/categories"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorCategories />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/profile"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorProfile />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/leads"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorLeads />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/vendor/bookings"
                                    element={
                                        <ProtectedRoute roles={["vendor", "admin"]}>
                                            <VendorBookings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/chat"
                                    element={
                                        <ProtectedRoute>
                                            <ChatPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/chat/:leadId"
                                    element={
                                        <ProtectedRoute>
                                            <ChatPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/get-started"
                                    element={
                                        <ProtectedRoute>
                                            <GetStartedWrapper />
                                        </ProtectedRoute>
                                    }
                                />
                                {/* Error routes */}
                                <Route path="/403" element={<Forbidden />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Layout>
                    } />
                </Routes>
                </div>
                </ImagePickerProvider>
                </FoodThemeProvider>
                </ToastProvider>
                                    </AISupportProvider>
                </OrderTrackingProvider>
                </SearchProvider>
                </PaymentProvider>
                </WishlistProvider>
                </CartProvider>
            </AuthProvider>
        </Provider>
    );
}

export default App;
